/* ═══════════════════════════════════════════════════════════════
   The Designer — Word-grade tables (contract §4 R7).

   SPEC cut-list (written first, per the R7 brief):

   IN SCOPE
     · Real TipTap/ProseMirror table nodes — @tiptap/extension-table(+
       -row/-header/-cell), NOT the old atom/contentEditable-div table.
       Kept at their OFFICIAL node names (table/tableRow/tableHeader/
       tableCell): prosemirror-tables and @tiptap/extension-table hardcode
       those names in a couple of internal spots (TableRow's own content
       expression, the "select-all-cells then Backspace" shortcut), so
       renaming them would silently break those paths. Nothing else in
       the framework cares about the name — every command/plugin resolves
       nodes generically via `tableRole`, which our attrs still carry.
     · One new node, `specCellParagraph`, is the ONLY editable text
       container inside a cell — same three marks (bold/italic/underline)
       plus highlight as everywhere else in the Designer, no font/size/
       colour path, matching schema.ts's founding rule.
     · Table-or-paragraph placement: `numbered` boolean attr on the table
       node. Numbered → the table carries the level's MasterFormat number
       (docx: an anchor paragraph with the number and no text, table
       indented to that level's text indent, immediately after).
       Standalone (default) → sits at the margin between paragraphs, no
       number, exactly like every table that existed before this field.
       Switchable after insertion via the table toolbar.
     · Row/column insert/delete, resize (framework's columnResizing),
       merge/split cells, toggle header row (repeats on export), per-cell
       alignment — all the FRAMEWORK's own commands (insertTable /
       addColumnBefore / addColumnAfter / deleteColumn / addRowBefore /
       addRowAfter / deleteRow / deleteTable / mergeCells / splitCell /
       toggleHeaderRow / setCellAttribute). This module supplies zero
       hand-rolled editing logic — only attrs, conversion, and a NodeView
       whose sole job is visual (colgroup %, border/shading CSS vars).
     · TableData round-trip: `TableCell` gains `runs`/`highlight` (mirrors
       ContentBlock exactly — same fold/unfold math, shared/text-runs.ts);
       `TableData` gains `numbered`. Everything else (colWidths, borders,
       header shading, justify) rides as the table node's `tableStyle`
       attr, re-derived into TableData at every save.
     · Cells accept `specCellParagraph+` (one OR MORE), not exactly one:
       the framework's own `mergeCells` command concatenates non-empty
       cells' content into the surviving cell, and "exactly one paragraph"
       would make that produce a schema-invalid document. Extra paragraphs
       therefore round-trip (joined with `\n`, matching how the harvester
       already joins multi-paragraph Word cells) but are NOT a designed
       authoring feature — no "add paragraph" UI, Enter inside a cell just
       does whatever the framework's default split does.

   ADDED — Phase 1 item 3 (MASTERS_UNIFIED_PLAN, 2026-08-25)
     · Per-cell SHADING is now reachable (the attrs existed since R7; the UI
       did not) from a small document palette, and per-cell BORDERS arrive as
       a new `borders` attr / `TableCell.borders` field: absent side = the
       table's own grid, exactly as before, so nothing that already exists
       changes shape. Still zero hand-rolled table logic — the structural
       commands are the framework's, this module only carries attrs.

   OUT OF SCOPE (cut, not silently dropped — matches the R7 brief verbatim)
     · Nested tables, per-cell fonts/sizes, a table-style gallery, vertical
       merge UI beyond the framework's own rowSpan merge/split, cell
       margins UI, autofit modes.
     · A forma-level "table style" source of truth does not exist yet
       (grepped: no such field on StandardConfig). Borders/shading stay
       PER-TABLE, exactly as they already were pre-R7 (BlockEditor's table
       properties panel, the harvester's imported defaults) — flagged in
       the R7 report as a gap to close in a future forma-styles pass, not
       solved here.
     · The framework's Backspace/Delete "delete table when every cell is
       selected" shortcut is hardcoded to the default node names and will
       not fire under any renamed schema — moot here since we kept the
       default names, but noted because the OFFICIAL "Delete table" command
       (wired to our toolbar button) is the one this build guarantees.
   ═══════════════════════════════════════════════════════════════ */

import { Node as TipTapNode, mergeAttributes } from '@tiptap/core';
import type { JSONContent } from '@tiptap/core';
import Table, { createTable } from '@tiptap/extension-table';
import type { TableOptions } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import type { Node as PMNode, Schema } from '@tiptap/pm/model';
import { columnResizing, columnResizingPluginKey, tableEditing, TableMap } from '@tiptap/pm/tables';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { EditorView, NodeView } from '@tiptap/pm/view';
import type {
  BorderLine,
  CellBorderValue,
  ContentBlock,
  TableCellBorders,
  TableCell as TableCellData,
  TableData,
  TableEdge,
} from '../../shared/types';
import { isBorderLine, resolveCellSide, type ResolvedBorder } from '../../shared/types';
import {
  effectiveRunsGeneric,
  foldRunsGeneric,
  nodeRunsFromJSON,
  type MarkFlags,
} from '../../shared/text-runs';
import { blockAttrs, newBlockId, type BlockMeta } from './nodeBase';

/** The table node's own name — kept at the framework default (see cut-list
 *  above). Exported so the rest of the Designer never re-types the string. */
export const TABLE_NODE_NAME = 'table';

// ─── Table-level style (everything a TableData carries EXCEPT the grid
//     itself — rows/cols/cells/headerRows are always derived from the live
//     node tree, never trusted from a stale attr). ───

export interface TableStyleAttrs {
  colWidths?: number[];
  /** Per-edge table lines (T1.1) — see TableData.edges. Absent edge ⇒ the
   *  legacy fields below. */
  edges?: Partial<Record<TableEdge, BorderLine>>;
  borderWidth?: number;
  borderColor?: string;
  borderStyle?: 'solid' | 'dashed' | 'dotted' | 'double' | 'none';
  innerBorderWidth?: number;
  innerBorderColor?: string;
  innerBorderStyle?: 'solid' | 'dashed' | 'dotted' | 'none';
  headerBgColor?: string;
  headerBgOpacity?: number;
  tableJustify?: 'left' | 'center' | 'right';
  /** EXPLICIT autofit — see TableData.autofit. Never inferred from a
   *  missing `colWidths`. */
  autofit?: boolean;
  /** The table's own width as a % of the text column. Absent ⇒ 100. */
  width?: number;
  /** Cell padding in points, all four sides, table-wide. Absent ⇒ Word's
   *  own default margins (and the sheet's own token padding on screen). */
  cellPadding?: number;
}

const TABLE_STYLE_KEYS = [
  'colWidths',
  'edges',
  'borderWidth',
  'borderColor',
  'borderStyle',
  'innerBorderWidth',
  'innerBorderColor',
  'innerBorderStyle',
  'headerBgColor',
  'headerBgOpacity',
  'tableJustify',
  'autofit',
  'width',
  'cellPadding',
] as const;

/** The same starter look BlockEditor and the old Designer atom gave a new
 *  table, so a table born here prints identically to one born anywhere
 *  else. These hex values are DOCUMENT data (what the .docx borders print
 *  as), not UI chrome. */
export const DEFAULT_NEW_TABLE_STYLE: TableStyleAttrs = {
  borderWidth: 1,
  borderColor: '000000',
  borderStyle: 'solid',
  innerBorderWidth: 0.5,
  innerBorderColor: '999999',
  innerBorderStyle: 'solid',
  headerBgColor: 'E8E8E8',
  headerBgOpacity: 100,
  tableJustify: 'left',
};

// ─── Border pens (T1.1) — ONE table, shared by the toolbox, the right-click
//     menu, the commands and the tests. Word's own defaults: ½ pt single
//     grid, 2¼ pt "thick". Export writes `w:sz` = pt × 8 from these same
//     numbers (Thick = SINGLE sz 18) and import reads them back ÷ 8. ───

export type BorderPenId = 'normal' | 'thick' | 'double' | 'dashed' | 'dotted' | 'none';

export interface BorderPen {
  id: BorderPenId;
  label: string;
  line: BorderLine;
}

export const BORDER_PENS: ReadonlyArray<BorderPen> = [
  { id: 'normal', label: 'Normal', line: { style: 'solid', width: 0.5 } },
  { id: 'thick', label: 'Thick', line: { style: 'solid', width: 2.25 } },
  { id: 'double', label: 'Double', line: { style: 'double', width: 0.5 } },
  { id: 'dashed', label: 'Dashed', line: { style: 'dashed', width: 0.5 } },
  { id: 'dotted', label: 'Dotted', line: { style: 'dotted', width: 0.5 } },
  { id: 'none', label: 'None', line: { style: 'none', width: 0 } },
];

export function borderPen(id: BorderPenId): BorderPen {
  return BORDER_PENS.find((p) => p.id === id) || BORDER_PENS[0];
}

/** The pen the author last picked — one per app session (not persisted),
 *  shared by the toolbox and the right-click menu so both stamp the same
 *  line. Defaults to Normal. */
let currentPen: BorderPenId = 'normal';
const penListeners = new Set<() => void>();
export function getBorderPen(): BorderPenId {
  return currentPen;
}
export function setBorderPen(id: BorderPenId): void {
  if (id === currentPen) return;
  currentPen = id;
  penListeners.forEach((fn) => fn());
}
export function subscribeBorderPen(fn: () => void): () => void {
  penListeners.add(fn);
  return () => {
    penListeners.delete(fn);
  };
}

/** Where a border command stamps the pen — Word's Borders menu, in order. */
export type BorderTarget =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'insideH'
  | 'insideV'
  | 'all'
  | 'outside'
  | 'inside'
  | 'none';

export const BORDER_TARGETS: ReadonlyArray<{ id: BorderTarget; label: string }> = [
  { id: 'top', label: 'Top Border' },
  { id: 'bottom', label: 'Bottom Border' },
  { id: 'left', label: 'Left Border' },
  { id: 'right', label: 'Right Border' },
  { id: 'insideH', label: 'Inside Horizontal Border' },
  { id: 'insideV', label: 'Inside Vertical Border' },
  { id: 'all', label: 'All Borders' },
  { id: 'outside', label: 'Outside Borders' },
  { id: 'inside', label: 'Inside Borders' },
  { id: 'none', label: 'No Border' },
];

// ─── Column widths — ONE source of truth (T1.1) ───
//
//     `tableStyle.colWidths` (percentages of the table's own width) is what
//     the document stores, what a variant/module carries, and what the .docx
//     writer turns into `w:tcW`. The framework's resizer speaks per-cell
//     `colwidth` PIXELS instead, so a drag is folded back into percentages the
//     moment it lands (columnWidthNormalizer below) and the pixels are dropped.
//     Nothing downstream ever sees two units at once.

/** The smallest share a drag may squeeze a column to. */
const MIN_COL_PCT = 4;

/** Percentages that sum to exactly 100, to two decimals. */
function to100(list: number[]): number[] {
  const total = list.reduce((s, w) => s + w, 0) || 1;
  const scaled = list.map((w) => Math.round((w / total) * 10000) / 100);
  const drift = Math.round((100 - scaled.reduce((s, w) => s + w, 0)) * 100) / 100;
  if (drift) {
    let big = 0;
    scaled.forEach((w, i) => {
      if (w > scaled[big]) big = i;
    });
    scaled[big] = Math.round((scaled[big] + drift) * 100) / 100;
  }
  return scaled;
}

/** Equal shares for `cols` columns, summing to 100 — "distribute evenly". */
export const evenColPct = (cols: number): number[] => to100(new Array(Math.max(1, cols)).fill(1));

/** A table narrower than this is not a table any more; wider than the text
 *  column is not printable. Shared by the command and the NodeView. */
export const MIN_TABLE_WIDTH_PCT = 10;
export const MAX_TABLE_WIDTH_PCT = 100;

/** The table's own width as a printable percentage — absent ⇒ full width,
 *  which is what every table did before `width` existed. */
export function tableWidthPct(width: number | undefined): number {
  if (typeof width !== 'number' || !Number.isFinite(width)) return MAX_TABLE_WIDTH_PCT;
  return Math.min(Math.max(Math.round(width), MIN_TABLE_WIDTH_PCT), MAX_TABLE_WIDTH_PCT);
}

/** A COMPLETE, sane percentage for every one of `cols` columns — or null when
 *  the table has no authored widths (or only some of them), which is the only
 *  case where the framework's own pixel/min-width layout is used instead. */
export function completeColPct(list: number[] | undefined, cols: number): number[] | null {
  if (!list || cols <= 0 || list.length < cols) return null;
  const take = list.slice(0, cols);
  if (take.some((w) => !Number.isFinite(w) || w <= 0)) return null;
  return to100(take);
}

/** One dragged border moves width between THAT column and its neighbour only.
 *  Every other column keeps the share it had (Word's behaviour), and the set
 *  still sums to 100 — so a resize never quietly re-proportions a column the
 *  user did not touch, and the exported `w:tcW` stays honest. */
export function resizeColPct(prev: number[], col: number, nextPct: number): number[] {
  const out = prev.slice();
  if (out.length < 2 || col < 0 || col >= out.length) return to100(out);
  const mate = col + 1 < out.length ? col + 1 : col - 1;
  const pair = out[col] + out[mate];
  // Two columns that between them hold less than two minimums cannot BOTH
  // clear the minimum — clamping then pushes one under it. Split what there
  // is instead, so neither column is squeezed further than the other.
  const taken =
    pair >= MIN_COL_PCT * 2
      ? Math.min(Math.max(nextPct, MIN_COL_PCT), pair - MIN_COL_PCT)
      : pair / 2;
  out[col] = taken;
  out[mate] = pair - taken;
  return to100(out);
}

/** Live per-column pixels off a table node's FIRST row (0 = never dragged),
 *  one entry per grid column, colspans expanded. */
function rowColPx(table: PMNode): number[] {
  const row = table.firstChild;
  const px: number[] = [];
  if (!row) return px;
  row.forEach((cell) => {
    const colspan = (cell.attrs.colspan as number) || 1;
    const colwidth = cell.attrs.colwidth as number[] | null;
    for (let j = 0; j < colspan; j++) px.push((colwidth && colwidth[j]) || 0);
  });
  return px;
}

/** Fold live pixels into the percentage set they came from.
 *  `totalPx` is the width those pixels were measured against (the table's own
 *  box, in the same units the framework's resizer used). */
export function foldColPx(
  prevPct: number[] | undefined,
  livePx: number[],
  changed: number[],
  totalPx: number
): number[] {
  const cols = livePx.length;
  if (livePx.every((w) => w > 0) && !changed.length) return to100(livePx);
  let pct = completeColPct(prevPct, cols) ?? evenColPct(cols);
  const moved = changed.length ? changed : livePx.map((w, i) => (w ? i : -1)).filter((i) => i >= 0);
  // No measurable table (a headless round trip, a detached view): infer the
  // width from the columns whose share is still known, rather than averaging
  // the untouched ones away.
  const known = moved.reduce((s, i) => s + pct[i], 0);
  const basis =
    totalPx > 0
      ? totalPx
      : known > 0
        ? moved.reduce((s, i) => s + livePx[i], 0) / (known / 100)
        : 0;
  if (basis <= 0) return pct;
  for (const i of moved) pct = resizeColPct(pct, i, (livePx[i] / basis) * 100);
  return pct;
}

// ─── The table's NodeView — VISUAL ONLY. contentDOM is a plain <tbody>;
//     every keystroke, selection and structural edit stays ProseMirror's.
//     The only thing this class does the framework's own TableView (which
//     it closely mirrors) doesn't is (a) fall back to `tableStyle.colWidths`
//     percentages for columns the user has never dragged, and (b) paint the
//     table's border/shading/justify from `tableStyle` via CSS variables.
//     Defined BEFORE `SpecTable` below: `Table.extend()` calls `addOptions()`
//     (which references this class) eagerly, at module-evaluation time. ───

/** A CSS double line needs three device pixels (line · gap · line) to show
 *  as two lines at all; Word's ½ pt double would collapse to one line at 90%
 *  zoom. So a double is drawn at its own width OR 3px, whichever is larger —
 *  computed here, per cell, like every other document width (the stylesheet
 *  carries no raw px, DESIGN §2). Export still writes the real ½ pt. */
const DOUBLE_MIN_PX = 3;

function borderCss(
  widthPt: number | undefined,
  style: string | undefined,
  color: string | undefined
): string {
  if (!widthPt || widthPt <= 0 || style === 'none') return 'none';
  const ink = `#${(color || '000000').replace('#', '')}`;
  if (style === 'double') return `max(${widthPt}pt, ${DOUBLE_MIN_PX}px) double ${ink}`;
  return `${widthPt}pt ${style || 'solid'} ${ink}`;
}

function resolvedCss(b: ResolvedBorder): string {
  return borderCss(b.width, b.style, b.color);
}

export function blendToWhite(hex: string, opacityPct?: number): string {
  const clean = (hex || '').replace('#', '').padEnd(6, '0').slice(0, 6);
  const o = Math.max(0, Math.min(100, opacityPct ?? 100)) / 100;
  if (o >= 1) return `#${clean}`;
  let out = '';
  for (let i = 0; i < 3; i++) {
    const c = parseInt(clean.slice(i * 2, i * 2 + 2), 16) || 0;
    out += Math.round(c * o + 255 * (1 - o))
      .toString(16)
      .padStart(2, '0');
  }
  return `#${out}`;
}

/** Ink that stays readable ON a document-coloured fill.
 *
 *  A cell's shading is DOCUMENT data — the colour it prints — but the text on
 *  top of it is the app's theme ink, and in dark mode that is light. Light ink
 *  on a light printed fill is unreadable (WCAG 4.5:1, DESIGN §7), so a shaded
 *  cell carries ink derived from its own fill instead. These two constants are
 *  the same class of value as DEFAULT_NEW_TABLE_STYLE's hexes: document ink,
 *  not UI chrome. */
const DOC_INK_DARK = '#1a1a1a';
const DOC_INK_LIGHT = '#ffffff';

export function inkOn(cssColor: string): string {
  const clean = (cssColor || '').replace('#', '').padEnd(6, '0').slice(0, 6);
  const [r, g, b] = [0, 1, 2].map((i) => (parseInt(clean.slice(i * 2, i * 2 + 2), 16) || 0) / 255);
  // Rec. 709 relative luminance — the standard "is this light or dark" test.
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.55 ? DOC_INK_DARK : DOC_INK_LIGHT;
}

/** Table element → its NodeView, so a column drag can hand the view the
 *  percentage set to preview without going through a document change. */
const TABLE_VIEWS = new WeakMap<HTMLTableElement, SpecTableView>();

export class SpecTableView implements NodeView {
  dom: HTMLElement;
  contentDOM: HTMLElement;
  private node: PMNode;
  private cellMinWidth: number;
  private table: HTMLTableElement;
  private colgroup: HTMLElement;
  /** A column drag in flight: the COMPLETE percentage set the release will
   *  write (Tweaks 1a). While set, it is the only width the colgroup shows. */
  private preview: number[] | null = null;

  constructor(node: PMNode, cellMinWidth: number, _view: EditorView) {
    this.node = node;
    this.cellMinWidth = cellMinWidth || 40;
    this.dom = document.createElement('div');
    this.dom.className = 'tableWrapper dsg-real-table-wrap';
    this.table = this.dom.appendChild(document.createElement('table'));
    this.table.className = 'dsg-real-table';
    this.colgroup = this.table.appendChild(document.createElement('colgroup'));
    this.contentDOM = this.table.appendChild(document.createElement('tbody'));
    TABLE_VIEWS.set(this.table, this);
    this.render();
  }

  /** Show `pct` as the column widths until cleared — the drag preview. */
  setPreview(pct: number[] | null) {
    this.preview = pct;
    this.updateColgroup(this.node);
  }

  private render() {
    this.applyStyleVars(this.node);
    this.updateColgroup(this.node);
  }

  private updateColgroup(node: PMNode) {
    const row = node.firstChild;
    if (!row) {
      this.colgroup.innerHTML = '';
      return;
    }
    const live = rowColPx(node);
    const anyLive = live.some((w) => w > 0);
    // ONE unit, never a mix. A colgroup of `285px, 33%, 34%` does not lay out
    // where it says: the browser rescales the whole row to fit, so a finished
    // drag landed somewhere other than where it was dropped (T1.1). A complete
    // percentage set always wins — it is the ONLY width the document stores
    // and the one the .docx exports — and per-cell px (a drag mid-flight, or a
    // pasted table) are used only while no complete set exists.
    const preview = this.preview && this.preview.length === live.length ? this.preview : null;
    const pct =
      preview ??
      completeColPct(
        (node.attrs.tableStyle as TableStyleAttrs | undefined)?.colWidths,
        live.length
      );
    // The <col> elements are REUSED and only their style rewritten: a drag
    // preview runs outside a ProseMirror update, and replacing the colgroup's
    // children there read as a foreign DOM change that re-rendered the whole
    // table mid-drag (Tweaks 1a).
    const cols = this.colgroup.children;
    while (cols.length > live.length) this.colgroup.lastChild?.remove();
    while (cols.length < live.length) this.colgroup.appendChild(document.createElement('col'));
    let col = 0;
    row.forEach((cell) => {
      const colspan = (cell.attrs.colspan as number) || 1;
      const colwidth = cell.attrs.colwidth as number[] | null;
      for (let j = 0; j < colspan; j++, col++) {
        const colEl = cols[col] as HTMLElement;
        const px = colwidth && colwidth[j];
        colEl.style.width = pct ? `${pct[col]}%` : px ? `${px}px` : '';
        colEl.style.minWidth = pct || px ? '' : `${this.cellMinWidth}px`;
      }
    });
    // `auto` — every column sized by its content — is what the author ASKED
    // for (autofit), never what a missing width set implies.
    const autofit = !!(node.attrs.tableStyle as TableStyleAttrs | undefined)?.autofit;
    // A drag ends autofit on release, so its preview is already laid out the
    // way the release will be: fixed.
    this.table.style.tableLayout = preview || (!autofit && (pct || anyLive)) ? 'fixed' : 'auto';
    // Defensive: nothing writes an inline min-width here any more (the
    // framework's px preview is replaced by the drag below), but a stale one
    // would hold the table at a dragged column's size.
    this.table.style.minWidth = '';
  }

  private applyStyleVars(node: PMNode) {
    const style = (node.attrs.tableStyle || {}) as TableStyleAttrs;
    const inner = borderCss(
      style.innerBorderWidth ?? 0.5,
      style.innerBorderStyle || 'solid',
      style.innerBorderColor || '999999'
    );
    this.table.style.setProperty('--dsg-tbl-inner', inner);
    // The table's own width across the page. A column drag never touches it
    // (the preview is a percentage set inside it), so no drag-time pin is
    // needed any more (Tweaks 1a).
    this.table.style.width = `${tableWidthPct(style.width)}%`;
    // Cell padding is DOCUMENT data in points — the same points Word prints.
    if (typeof style.cellPadding === 'number')
      this.table.style.setProperty('--sf-cell-pad', `${Math.max(0, style.cellPadding)}pt`);
    else this.table.style.removeProperty('--sf-cell-pad');
    const headerBg = style.headerBgColor
      ? blendToWhite(style.headerBgColor, style.headerBgOpacity ?? 100)
      : null;
    this.table.style.setProperty('--dsg-tbl-header-bg', headerBg || 'transparent');
    // A shaded header row prints its own colour, so its text needs ink that
    // survives the theme too (dark mode's light ink on a light printed band
    // was unreadable) — same rule as a shaded cell, see inkOn.
    this.table.style.setProperty('--dsg-tbl-header-ink', headerBg ? inkOn(headerBg) : 'inherit');
    // Every line — outer AND inside — is drawn by the cells themselves
    // (tableBorderDecorations below resolves each cell side: cell override →
    // table edge → legacy). A border on the <table> element would out-weigh a
    // cell's "no line" under border-collapse, so the table draws none.
    this.table.style.border = 'none';
    this.table.classList.toggle('dsg-tbl-numbered', !!node.attrs.numbered);
    this.dom.style.margin =
      style.tableJustify === 'center'
        ? '0 auto'
        : style.tableJustify === 'right'
          ? '0 0 0 auto'
          : '0';
  }

  update(node: PMNode) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  ignoreMutation(mutation: { type: string; target: Node }) {
    // The colgroup and the table's own style are this view's, never the
    // document's — ANY change to them (a drag preview adding a <col> too)
    // must not be read back as an edit, or ProseMirror re-renders the table
    // and a drag in flight loses its element (Tweaks 1a).
    if (mutation.type === 'selection') return false;
    return (
      (mutation.type === 'attributes' && mutation.target === this.table) ||
      this.colgroup.contains(mutation.target)
    );
  }

  destroy() {
    TABLE_VIEWS.delete(this.table);
  }
}

// ─── Nodes ───

/** The ONLY editable text container inside a cell. Same three marks +
 *  highlight as specParagraph — no font/size/colour path here either. */
export const SpecCellParagraph = TipTapNode.create({
  name: 'specCellParagraph',
  group: 'block',
  content: 'inline*',
  parseHTML: () => [{ tag: 'p[data-cell-p]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'p',
    mergeAttributes(HTMLAttributes, { 'data-cell-p': '' }),
    0,
  ],
});

/** Style attrs every cell (data or header) carries on top of the framework's
 *  own colspan/rowspan/colwidth. */
const CELL_STYLE_ATTRS = {
  align: { default: null as 'left' | 'center' | 'right' | null },
  /** Where the cell's content sits in its own box. null ⇒ top, which is
   *  what every cell did before this attr existed. */
  valign: { default: null as 'top' | 'middle' | 'bottom' | null },
  bgColor: { default: null as string | null },
  bgOpacity: { default: null as number | null },
  /** Per-side border overrides — see TableCellBorders. An absent side keeps
   *  the table's own grid line, which is what every cell did before this
   *  attr existed. */
  borders: { default: null as TableCellBorders | null },
};

export const CELL_BORDER_SIDES = ['top', 'right', 'bottom', 'left'] as const;
export type CellBorderSide = (typeof CELL_BORDER_SIDES)[number];

/** One vocabulary for the four sides, shared by the toolbar and the menu. */
export const CELL_BORDER_SIDE_LABELS: Record<CellBorderSide, string> = {
  top: 'Top',
  right: 'Right',
  bottom: 'Bottom',
  left: 'Left',
};

/** Drop empty/undefined sides so "no override" is always stored as `null`,
 *  never as `{}` — a `{}` would round-trip into TableData as noise. */
export function normalizeCellBorders(
  b: TableCellBorders | null | undefined
): TableCellBorders | null {
  if (!b) return null;
  const out: TableCellBorders = {};
  let any = false;
  for (const side of CELL_BORDER_SIDES) {
    const v: unknown = b[side];
    if (typeof v === 'boolean') {
      out[side] = v;
      any = true;
    } else if (isBorderLine(v)) {
      // A stored line keeps exactly its two fields — never stray keys.
      out[side] =
        v.style === 'none' ? { style: 'none', width: 0 } : { style: v.style, width: v.width };
      any = true;
    }
  }
  return any ? out : null;
}

/** The cell-shading palette the table UI offers. These hex values are
 *  DOCUMENT data — what the .docx cell shading prints as — exactly the same
 *  class of value as DEFAULT_NEW_TABLE_STYLE's, not UI chrome (DESIGN §2
 *  governs the app's own surfaces). They mirror the app's neutral ramp plus
 *  one accent tint, so a shaded table still looks like it belongs here. */
export const CELL_SHADES: ReadonlyArray<{ hex: string | null; label: string }> = [
  { hex: null, label: 'No fill' },
  { hex: 'FFFFFF', label: 'White' },
  { hex: 'F1F3F5', label: 'Lightest gray' },
  { hex: 'E9ECEF', label: 'Light gray' },
  { hex: 'DEE2E6', label: 'Gray' },
  { hex: 'CED4DA', label: 'Medium gray' },
  { hex: 'ADB5BD', label: 'Dark gray' },
  { hex: 'D0EBFF', label: 'Accent' },
];

function cellHtmlAttrs(node: PMNode): Record<string, string> {
  const align = node.attrs.align as string | null;
  const valign = node.attrs.valign as string | null;
  const bgColor = node.attrs.bgColor as string | null;
  const bgOpacity = node.attrs.bgOpacity as number | null;
  const borders = node.attrs.borders as TableCellBorders | null;
  const style: string[] = [];
  if (align) style.push(`text-align:${align}`);
  if (valign) style.push(`vertical-align:${valign}`);
  if (bgColor) {
    const fill = blendToWhite(bgColor, bgOpacity ?? 100);
    style.push(`background:${fill}`);
    // Readable on the fill in BOTH themes — see inkOn.
    style.push(`color:${inkOn(fill)}`);
  }
  // The HTML form (clipboard, getHTML) of an override. `--dsg-tbl-inner` is
  // the table's own grid line (set per table by the NodeView), so "on" means
  // the same line the rest of the table draws. In the live editor every side
  // is re-resolved exactly — with the table's edges and colours — by
  // tableBorderDecorations, which lands after (and so wins over) this.
  for (const side of CELL_BORDER_SIDES) {
    const v: CellBorderValue | undefined = borders?.[side];
    if (v === undefined) continue;
    if (typeof v === 'boolean') {
      style.push(`border-${side}:${v ? 'var(--dsg-tbl-inner)' : 'none'}`);
    } else {
      style.push(`border-${side}:${borderCss(v.width, v.style, '000000')}`);
    }
  }
  return style.length ? { style: style.join(';') } : {};
}

/** A row carries ONE attr of our own: a MINIMUM height in points (Word's
 *  `w:trHeight` with `hRule="atLeast"`). null — the default — is the row
 *  sizing to its content, exactly what every row did before this attr. */
export const SpecTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      minHeight: { default: null as number | null, renderHTML: () => ({}) },
    };
  },
  renderHTML({ HTMLAttributes, node }) {
    const pt = node.attrs.minHeight as number | null;
    return ['tr', mergeAttributes(HTMLAttributes, pt ? { style: `height:${pt}pt` } : {}), 0];
  },
});

export const SpecTableCell = TableCell.extend({
  content: 'specCellParagraph+',
  addAttributes() {
    return { ...this.parent?.(), ...CELL_STYLE_ATTRS };
  },
  renderHTML({ HTMLAttributes, node }) {
    return ['td', mergeAttributes(HTMLAttributes, cellHtmlAttrs(node)), 0];
  },
});

export const SpecTableHeader = TableHeader.extend({
  content: 'specCellParagraph+',
  addAttributes() {
    return { ...this.parent?.(), ...CELL_STYLE_ATTRS };
  },
  renderHTML({ HTMLAttributes, node }) {
    return ['th', mergeAttributes(HTMLAttributes, cellHtmlAttrs(node)), 0];
  },
});

/** The width of the box a table's own pixels were measured against — its
 *  NodeView wrapper, NOT the table element: the framework's drag preview
 *  shrinks the table itself mid-drag, and `clientWidth` keeps the same units
 *  the resizer's `offsetWidth` math used (so a zoomed sheet still folds
 *  exactly where the border was dropped). */
function tableBoxWidth(view: EditorView | null, pos: number): number {
  if (!view) return 0;
  try {
    const dom = view.nodeDOM(pos);
    return dom instanceof HTMLElement ? dom.clientWidth || 0 : 0;
  } catch {
    return 0;
  }
}

/** Every finished drag becomes percentages, in the SAME undo step.
 *
 *  prosemirror-tables commits a resize as pixels on one column's cells and
 *  leaves every other column null. Left that way the document held two units
 *  at once: the colgroup rendered `285px, 33%, 34%` (so the column did not
 *  land where it was dropped), and the save/reopen rebuild — which can only
 *  carry percentages — fell back to an AVERAGE that flattened every column the
 *  user never touched. This folds the pixels into a complete percentage set
 *  the moment they arrive and clears them, so the live sheet, a reopened
 *  master, a variant and the .docx all read the same widths. */
export function columnWidthNormalizer(): Plugin {
  let live: EditorView | null = null;
  return new Plugin({
    key: new PluginKey('specTableColumnWidths'),
    view: (v) => {
      live = v;
      // (The drag-time `dsg-tbl-dragging` width pin that used to live here is
      // gone: a column drag no longer writes inline widths — Tweaks 1a.)
      return {
        destroy: () => {
          if (live === v) live = null;
        },
      };
    },
    appendTransaction: (trs, oldState, newState) => {
      if (!trs.some((t) => t.docChanged)) return null;
      // Keyed by POSITION, mapped forward through this batch — not by blockId:
      // a paste or a duplicate can put two tables on the same id for one tick
      // (that is what UniqueBlockIds exists to repair), and a blank id would
      // make them share a key and fold each other's pixels.
      const before = new Map<number, number[]>();
      oldState.doc.forEach((n, oldPos) => {
        if (n.type.name !== TABLE_NODE_NAME) return;
        let pos = oldPos;
        for (const t of trs) {
          const mapped = t.mapping.mapResult(pos, 1);
          if (mapped.deleted) return;
          pos = mapped.pos;
        }
        before.set(pos, rowColPx(n));
      });
      let tr: Transaction | null = null;
      newState.doc.forEach((node, pos) => {
        if (node.type.name !== TABLE_NODE_NAME) return;
        const px = rowColPx(node);
        if (!px.some((w) => w > 0)) return;
        const was = before.get(pos) || [];
        const changed = px.map((w, i) => (w && w !== (was[i] || 0) ? i : -1)).filter((i) => i >= 0);
        const style = (node.attrs.tableStyle || {}) as TableStyleAttrs;
        const colWidths = foldColPx(style.colWidths, px, changed, tableBoxWidth(live, pos));
        tr = tr || newState.tr;
        // A drag IS a width the author chose — it ends autofit.
        const nextStyle = { ...style, colWidths };
        delete nextStyle.autofit;
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, tableStyle: nextStyle });
        node.descendants((child, childPos) => {
          if (child.type.name === 'tableRow') return true;
          if (child.attrs.colwidth)
            (tr as Transaction).setNodeMarkup(pos + 1 + childPos, undefined, {
              ...child.attrs,
              colwidth: null,
            });
          return false;
        });
      });
      return tr;
    },
  });
}

// ─── Dragging a border (Tweaks 1a column · 1b row) ───
//
//     Hand-built (Josh OK 2026-10-23): prosemirror-tables' own resizer reads
//     the mouse in SCREEN pixels and previews ONE column in px inside a
//     %-sized table, so under the canvas's `transform: scale(zoom × dial)` the
//     border lagged the cursor and the release re-laid the table out
//     differently. Its handle detection (which border is under the pointer),
//     its plugin state and its handle decoration stay in use; only the drag
//     itself is ours. Rows have no framework resizer at all.

/** Screen pixels per layout pixel — the sheet's effective zoom (zoom × the
 *  Interface dial, or any other transform), read off the element itself. */
export function effectiveScale(screenWidth: number, layoutWidth: number): number {
  if (!(screenWidth > 0) || !(layoutWidth > 0)) return 1;
  return screenWidth / layoutWidth;
}

/** Mouse travel in screen pixels → layout pixels on the sheet. */
export function screenToLayout(deltaScreen: number, scale: number): number {
  return scale > 0 && Number.isFinite(scale) ? deltaScreen / scale : deltaScreen;
}

/** The COMPLETE percentage set a column drag shows — and, unchanged, the set
 *  its release writes. `startPx` is the column's width at mousedown and
 *  `tablePx` the table's, both in layout pixels; the column never goes under
 *  `minPx` (the table's `cellMinWidth`). Folded by the SAME `foldColPx` a
 *  committed pixel width always went through. */
export function dragColPct(
  startPct: number[],
  col: number,
  startPx: number,
  deltaPx: number,
  tablePx: number,
  minPx: number
): number[] {
  const live = startPct.map(() => 0);
  live[col] = Math.max(minPx, startPx + deltaPx);
  return foldColPx(startPct, live, [col], tablePx);
}

/** CSS points per layout pixel (96 px = 72 pt). */
const PT_PER_PX = 0.75;
/** The Table properties "Row height" field's own bounds (whole points). */
const ROW_MIN_PT = 1;
const ROW_MAX_PT = 720;

/** The minimum row height (whole pt) a row drag shows and writes: the row's
 *  height at mousedown plus the travel, never under the row's content
 *  ("at least", like Word — a drag cannot clip text). */
export function dragRowPt(startPx: number, deltaPx: number, contentPx: number): number {
  const floor = Math.ceil(Math.max(0, contentPx) * PT_PER_PX);
  const want = Math.round((startPx + deltaPx) * PT_PER_PX);
  return Math.min(Math.max(want, floor, ROW_MIN_PT), ROW_MAX_PT);
}

interface FrameworkResizeState {
  activeHandle: number;
  dragging: unknown;
}

interface RowDragState {
  /** Absolute position of the row whose bottom border is under the pointer. */
  handle: number;
  dragging: boolean;
  /** The height (pt) the held row is shown at — and will be written as. */
  previewPt: number | null;
}

type RowDragMeta = Partial<RowDragState>;

const tableDragKey = new PluginKey<RowDragState>('specTableDrag');

function domCellAround(target: EventTarget | null): HTMLElement | null {
  let el = target as HTMLElement | null;
  while (el && el.nodeName !== 'TD' && el.nodeName !== 'TH') {
    if (el.classList?.contains('ProseMirror')) return null;
    el = el.parentElement;
  }
  return el;
}

function tableElAt(view: EditorView, tableStart: number): HTMLTableElement | null {
  let dom: Node | null = view.domAtPos(tableStart).node;
  while (dom && dom.nodeName !== 'TABLE') dom = dom.parentNode;
  return dom as HTMLTableElement | null;
}

/** Where a table node sits right now, found by its rendered element — a drag
 *  survives positions shifting under it (a collaborator typing above). */
function tablePosOf(view: EditorView, tableEl: HTMLTableElement): number {
  let found = -1;
  view.state.doc.descendants((node, pos) => {
    if (found >= 0) return false;
    if (node.type.name !== TABLE_NODE_NAME) return true;
    const dom = view.nodeDOM(pos);
    if (dom instanceof HTMLElement && dom.contains(tableEl)) found = pos;
    return false;
  });
  return found;
}

/** The rendered share of every grid column, for a table with no complete
 *  stored set (or an autofit one) — so the drag starts from what is on screen
 *  instead of jumping to even columns. */
function measuredColPct(tableEl: HTMLTableElement, cols: number): number[] | null {
  const row = tableEl.tBodies[0]?.rows[0];
  if (!row) return null;
  const px: number[] = [];
  for (const cell of Array.from(row.cells)) {
    const span = cell.colSpan || 1;
    for (let j = 0; j < span; j++) px.push(cell.offsetWidth / span);
  }
  return completeColPct(px, cols);
}

/** The cell (position relative to the table start) under a DOM cell. */
function cellInfo(view: EditorView, cellEl: HTMLElement) {
  let pos: number;
  try {
    pos = view.posAtDOM(cellEl, 0);
  } catch {
    return null;
  }
  const $pos = view.state.doc.resolve(pos);
  for (let d = $pos.depth; d > 1; d--) {
    const role = $pos.node(d).type.spec.tableRole;
    if (role !== 'cell' && role !== 'header_cell') continue;
    const table = $pos.node(d - 2);
    if (table.type.name !== TABLE_NODE_NAME) return null;
    const tableStart = $pos.start(d - 2);
    const map = TableMap.get(table);
    return { table, tableStart, map, rect: map.findCell($pos.before(d) - tableStart) };
  }
  return null;
}

/** Absolute position of row `index` in the table starting at `tableStart`. */
function rowPos(table: PMNode, tableStart: number, index: number): number {
  let pos = -1;
  let i = 0;
  table.forEach((_row, offset) => {
    if (i++ === index) pos = tableStart + offset;
  });
  return pos;
}

/** Which row's bottom border the pointer is on: within `handleWidth` screen
 *  px of a cell's bottom edge (that cell's last row) or of its top edge (the
 *  row above). -1 off any border — and on a COLUMN border, which keeps its
 *  own handle. */
function rowEdgeAt(view: EditorView, event: MouseEvent, handleWidth: number): number {
  const cellEl = domCellAround(event.target);
  if (!cellEl) return -1;
  const r = cellEl.getBoundingClientRect();
  if (event.clientX - r.left <= handleWidth || r.right - event.clientX <= handleWidth) return -1;
  const near =
    r.bottom - event.clientY <= handleWidth
      ? 'bottom'
      : event.clientY - r.top <= handleWidth
        ? 'top'
        : null;
  if (!near) return -1;
  const info = cellInfo(view, cellEl);
  if (!info) return -1;
  const index = near === 'bottom' ? info.rect.bottom - 1 : info.rect.top - 1;
  return index < 0 ? -1 : rowPos(info.table, info.tableStart, index);
}

/** The height a row's content needs, in layout px: the tallest single-row
 *  cell's content box plus its padding and (collapsed, so half) borders. */
function rowContentPx(rowEl: HTMLTableRowElement, scale: number): number {
  let most = 0;
  for (const cell of Array.from(rowEl.cells)) {
    if ((cell.rowSpan || 1) > 1) continue;
    const kids = Array.from(cell.children).filter(
      (k) =>
        !k.classList.contains('column-resize-handle') && !k.classList.contains('row-resize-handle')
    );
    const cs = getComputedStyle(cell);
    const num = (v: string) => parseFloat(v) || 0;
    const content = kids.length
      ? screenToLayout(
          kids[kids.length - 1].getBoundingClientRect().bottom -
            kids[0].getBoundingClientRect().top,
          scale
        )
      : 0;
    const box =
      content +
      num(cs.paddingTop) +
      num(cs.paddingBottom) +
      (num(cs.borderTopWidth) + num(cs.borderBottomWidth)) / 2;
    most = Math.max(most, box);
  }
  return most;
}

function rowHandleDecorations(doc: PMNode, st: RowDragState): DecorationSet {
  if (st.handle < 0) return DecorationSet.empty;
  const row = doc.nodeAt(st.handle);
  if (!row || row.type.spec.tableRole !== 'row') return DecorationSet.empty;
  const $row = doc.resolve(st.handle);
  const table = $row.parent;
  const tableStart = $row.start();
  let map: TableMap;
  try {
    map = TableMap.get(table);
  } catch {
    return DecorationSet.empty;
  }
  const index = $row.index();
  const decos: Decoration[] = [];
  const seen = new Set<number>();
  // Every cell whose bottom edge IS this row's bottom border wears the bar —
  // the same accent bar the column handle draws, laid on its side.
  for (let col = 0; col < map.width; col++) {
    const cellPos = map.map[index * map.width + col];
    if (seen.has(cellPos)) continue;
    seen.add(cellPos);
    if (map.findCell(cellPos).bottom - 1 !== index) continue;
    const cell = table.nodeAt(cellPos);
    if (!cell) continue;
    const bar = document.createElement('div');
    bar.className = 'row-resize-handle';
    decos.push(
      Decoration.widget(tableStart + cellPos + cell.nodeSize - 1, bar, {
        key: `row-resize-${cellPos}`,
      })
    );
  }
  if (st.dragging && st.previewPt !== null)
    decos.push(
      Decoration.node(st.handle, st.handle + row.nodeSize, { style: `height:${st.previewPt}pt` })
    );
  return DecorationSet.create(doc, decos);
}

/** Column drag (1a): the colgroup shows the exact % set the release writes,
 *  the mouse is read in layout px, one undo step. Returns true when it took
 *  the mousedown. */
function startColumnDrag(view: EditorView, event: MouseEvent, cellMinWidth: number): boolean {
  const rs = columnResizingPluginKey.getState(view.state) as FrameworkResizeState | undefined;
  if (!rs || rs.activeHandle < 0 || rs.dragging) return false;
  const $cell = view.state.doc.resolve(rs.activeHandle);
  const table = $cell.node(-1);
  const cell = $cell.nodeAfter;
  if (!table || !cell || table.type.name !== TABLE_NODE_NAME) return false;
  const tableStart = $cell.start(-1);
  const map = TableMap.get(table);
  const col = map.colCount($cell.pos - tableStart) + ((cell.attrs.colspan as number) || 1) - 1;
  const firstEl = tableElAt(view, tableStart);
  const firstView = firstEl ? TABLE_VIEWS.get(firstEl) : undefined;
  if (!firstEl || !firstView) return false;
  let tableEl: HTMLTableElement = firstEl;
  let tv: SpecTableView = firstView;
  /** The table's LIVE element and view. Should anything re-render the table
   *  mid-drag (a collaborator's edit), follow the framework's handle — which
   *  it maps through every change — to the new one instead of previewing
   *  into a detached element. */
  const current = () => {
    if (tableEl.isConnected) return;
    const h = (columnResizingPluginKey.getState(view.state) as FrameworkResizeState | undefined)
      ?.activeHandle;
    if (h === undefined || h < 0) return;
    const el = tableElAt(view, view.state.doc.resolve(h).start(-1));
    const v = el ? TABLE_VIEWS.get(el) : undefined;
    if (el && v) {
      tableEl = el;
      tv = v;
    }
  };
  const tablePx = tableEl.offsetWidth;
  if (tablePx <= 0) return false;
  const scale = effectiveScale(tableEl.getBoundingClientRect().width, tablePx);
  const style = (table.attrs.tableStyle || {}) as TableStyleAttrs;
  const stored = style.autofit ? null : completeColPct(style.colWidths, map.width);
  const startPct = stored ?? measuredColPct(tableEl, map.width) ?? evenColPct(map.width);
  const startPx = (startPct[col] / 100) * tablePx;
  const startX = event.clientX;
  let pct: number[] | null = null;
  // The framework's own `dragging` state keeps its handle bar lit and stops
  // it re-detecting a border while this drag is held.
  view.dispatch(
    view.state.tr.setMeta(columnResizingPluginKey, {
      setDragging: { startX, startWidth: startPx },
    })
  );
  const win = view.dom.ownerDocument.defaultView ?? window;
  const finish = () => {
    win.removeEventListener('mouseup', finish);
    win.removeEventListener('mousemove', move);
    current();
    const tr = view.state.tr.setMeta(columnResizingPluginKey, { setDragging: null });
    const pos = pct ? tablePosOf(view, tableEl) : -1;
    const node = pos >= 0 ? view.state.doc.nodeAt(pos) : null;
    const was = (node?.attrs.tableStyle || {}) as TableStyleAttrs;
    const same =
      !!pct &&
      !was.autofit &&
      pct.length === (was.colWidths?.length ?? -1) &&
      pct.every((w, i) => w === was.colWidths?.[i]);
    if (pct && node && !same) {
      // Exactly the set the preview showed — nothing moves on release. A
      // drag IS a width the author chose, so it ends autofit (as before).
      const nextStyle: TableStyleAttrs = { ...was, colWidths: pct };
      delete nextStyle.autofit;
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, tableStyle: nextStyle });
      node.descendants((child, childPos) => {
        if (child.type.name === 'tableRow') return true;
        if (child.attrs.colwidth)
          tr.setNodeMarkup(pos + 1 + childPos, undefined, { ...child.attrs, colwidth: null });
        return false;
      });
    }
    view.dispatch(tr);
    tv.setPreview(null);
  };
  const move = (e: MouseEvent) => {
    if (!e.buttons) return finish();
    const delta = screenToLayout(e.clientX - startX, scale);
    pct = dragColPct(startPct, col, startPx, delta, tablePx, cellMinWidth);
    current();
    tv.setPreview(pct);
  };
  tv.setPreview(startPct);
  win.addEventListener('mouseup', finish);
  win.addEventListener('mousemove', move);
  event.preventDefault();
  return true;
}

/** Row drag (1b): writes the row's `minHeight` (pt) — the Table properties
 *  "Row height" value — live under the cursor, one undo step. */
function startRowDrag(view: EditorView, event: MouseEvent): boolean {
  const st = tableDragKey.getState(view.state);
  if (!st || st.handle < 0 || st.dragging) return false;
  const rowEl = view.nodeDOM(st.handle);
  if (!(rowEl instanceof HTMLTableRowElement)) return false;
  const tableEl = rowEl.closest('table');
  if (!tableEl || tableEl.offsetWidth <= 0) return false;
  const scale = effectiveScale(tableEl.getBoundingClientRect().width, tableEl.offsetWidth);
  const startPx = rowEl.offsetHeight;
  const contentPx = rowContentPx(rowEl, scale);
  const startY = event.clientY;
  let moved = false;
  view.dispatch(
    view.state.tr.setMeta(tableDragKey, {
      dragging: true,
      previewPt: dragRowPt(startPx, 0, contentPx),
    } satisfies RowDragMeta)
  );
  const win = view.dom.ownerDocument.defaultView ?? window;
  const finish = () => {
    win.removeEventListener('mouseup', finish);
    win.removeEventListener('mousemove', move);
    const now = tableDragKey.getState(view.state);
    const tr = view.state.tr.setMeta(tableDragKey, {
      dragging: false,
      previewPt: null,
    } satisfies RowDragMeta);
    const row = now && now.handle >= 0 ? view.state.doc.nodeAt(now.handle) : null;
    if (moved && now && now.previewPt !== null && row?.type.spec.tableRole === 'row') {
      if (row.attrs.minHeight !== now.previewPt)
        tr.setNodeAttribute(now.handle, 'minHeight', now.previewPt);
    }
    view.dispatch(tr);
  };
  const move = (e: MouseEvent) => {
    if (!e.buttons) return finish();
    moved = true;
    const pt = dragRowPt(startPx, screenToLayout(e.clientY - startY, scale), contentPx);
    if (pt !== tableDragKey.getState(view.state)?.previewPt)
      view.dispatch(view.state.tr.setMeta(tableDragKey, { previewPt: pt } satisfies RowDragMeta));
  };
  win.addEventListener('mouseup', finish);
  win.addEventListener('mousemove', move);
  event.preventDefault();
  return true;
}

/** The hand-built half of table resizing — see the section comment above. */
export function tableDrag({
  cellMinWidth,
  handleWidth,
}: {
  cellMinWidth: number;
  handleWidth: number;
}): Plugin<RowDragState> {
  const setHandle = (view: EditorView, handle: number) => {
    if (tableDragKey.getState(view.state)?.handle !== handle)
      view.dispatch(view.state.tr.setMeta(tableDragKey, { handle } satisfies RowDragMeta));
  };
  return new Plugin<RowDragState>({
    key: tableDragKey,
    state: {
      init: () => ({ handle: -1, dragging: false, previewPt: null }),
      apply(tr, prev) {
        const meta = tr.getMeta(tableDragKey) as RowDragMeta | undefined;
        let next = meta ? { ...prev, ...meta } : prev;
        if (next.handle >= 0 && tr.docChanged) {
          const mapped = tr.mapping.mapResult(next.handle, 1);
          const row = mapped.deleted ? null : tr.doc.nodeAt(mapped.pos);
          next =
            row?.type.spec.tableRole === 'row'
              ? { ...next, handle: mapped.pos }
              : { handle: -1, dragging: false, previewPt: null };
        }
        return next;
      },
    },
    props: {
      attributes: (state): Record<string, string> =>
        (tableDragKey.getState(state)?.handle ?? -1) >= 0 ? { class: 'row-resize-cursor' } : {},
      decorations: (state) => {
        const st = tableDragKey.getState(state);
        return st ? rowHandleDecorations(state.doc, st) : DecorationSet.empty;
      },
      handleDOMEvents: {
        mousemove: (view, event) => {
          const st = tableDragKey.getState(view.state);
          // A held row owns the pointer — no column border lights up under it.
          if (st?.dragging) return true;
          if (!view.editable) return false;
          const col = columnResizingPluginKey.getState(view.state) as
            | FrameworkResizeState
            | undefined;
          setHandle(view, col?.dragging ? -1 : rowEdgeAt(view, event, handleWidth));
          return false;
        },
        mouseleave: (view) => {
          const st = tableDragKey.getState(view.state);
          if (st && st.handle >= 0 && !st.dragging) setHandle(view, -1);
          return false;
        },
        mousedown: (view, event) => {
          if (!view.editable) return false;
          return startColumnDrag(view, event, cellMinWidth) || startRowDrag(view, event);
        },
      },
    },
  });
}

/** Paint every cell side of every table from the document: cell override →
 *  table edge → legacy fallback (shared/spec.ts `resolveCellSide`). A node
 *  decoration, not a DOM write, so ProseMirror owns the cells' DOM and the
 *  merged-cell geometry comes from prosemirror-tables' own TableMap — a
 *  cell's side is "outer" only when it truly sits on the table's edge, even
 *  beside a rowspan/colspan. Recomputed only when the document changes. */
function tableBorderDecorationSet(doc: PMNode): DecorationSet {
  const decos: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name !== TABLE_NODE_NAME) return true;
    const style = (node.attrs.tableStyle || {}) as TableStyleAttrs;
    let map: TableMap;
    try {
      map = TableMap.get(node);
    } catch {
      return false;
    }
    const start = pos + 1;
    const seen = new Set<number>();
    for (const cellPos of map.map) {
      if (seen.has(cellPos)) continue;
      seen.add(cellPos);
      const cell = node.nodeAt(cellPos);
      if (!cell) continue;
      const r = map.findCell(cellPos);
      const own = normalizeCellBorders(cell.attrs.borders as TableCellBorders | null);
      const outer: Record<CellBorderSide, boolean> = {
        top: r.top === 0,
        bottom: r.bottom === map.height,
        left: r.left === 0,
        right: r.right === map.width,
      };
      const css = CELL_BORDER_SIDES.map(
        (side) =>
          `border-${side}:${resolvedCss(resolveCellSide(style, side, outer[side], own?.[side]))}`
      ).join(';');
      decos.push(Decoration.node(start + cellPos, start + cellPos + cell.nodeSize, { style: css }));
    }
    return false;
  });
  return DecorationSet.create(doc, decos);
}

export function tableBorderDecorations(): Plugin<DecorationSet> {
  return new Plugin<DecorationSet>({
    key: new PluginKey('specTableBorders'),
    state: {
      init: (_config, state) => tableBorderDecorationSet(state.doc),
      apply: (tr, old) => (tr.docChanged ? tableBorderDecorationSet(tr.doc) : old),
    },
    props: {
      decorations(state) {
        return this.getState(state);
      },
    },
  });
}

export const SpecTable = Table.extend({
  addProseMirrorPlugins() {
    // NOT `this.parent()`: the framework's Table installs `columnResizing` —
    // which is ALSO what mounts SpecTableView — only when the editor is
    // editable at creation. A canvas opened read-only (View mode, a variant
    // tab) therefore drew every table as a bare browser `<table>` (Tweaks 9).
    // The same two plugins are built here unconditionally; every resize
    // handler already checks `view.editable` itself, so a read-only canvas
    // still cannot drag. `tableDrag` comes FIRST so its mousedown answers a
    // held border before the framework's px resizer can (Tweaks 1a/1b).
    const o = this.options;
    return [
      ...(o.resizable
        ? [
            tableDrag({ cellMinWidth: o.cellMinWidth, handleWidth: o.handleWidth }),
            columnResizing({
              handleWidth: o.handleWidth,
              cellMinWidth: o.cellMinWidth,
              defaultCellMinWidth: o.cellMinWidth,
              View: o.View,
              lastColumnResizable: o.lastColumnResizable,
            }),
          ]
        : []),
      tableEditing({ allowTableNodeSelection: o.allowTableNodeSelection }),
      columnWidthNormalizer(),
      tableBorderDecorations(),
    ];
  },
  addOptions() {
    return {
      ...this.parent?.(),
      resizable: true,
      cellMinWidth: 40,
      handleWidth: 6,
      lastColumnResizable: true,
      View: SpecTableView as unknown as TableOptions['View'],
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      ...blockAttrs,
      text: { default: '' },
      /** §4 R7: numbered-under-level vs standalone. */
      numbered: { default: false },
      tableStyle: { default: {} as TableStyleAttrs },
      /** Forward-compat catch-all, mirrors schema.ts's paragraph `meta` —
       *  any TableData field this module doesn't know about yet rides here
       *  untouched instead of being silently dropped. */
      tableMeta: { default: {} as BlockMeta },
    };
  },
});

/** Every extension the table feature needs, in DESIGNER_NODES order. */
export const TABLE_NODES = [
  SpecTable,
  SpecTableRow,
  SpecTableHeader,
  SpecTableCell,
  SpecCellParagraph,
];

// ─── ContentBlock (tableData) → ProseMirror JSON ───

function marksForRun(flags: MarkFlags): JSONContent['marks'] {
  const marks: NonNullable<JSONContent['marks']> = [];
  if (flags.bold) marks.push({ type: 'bold' });
  if (flags.italic) marks.push({ type: 'italic' });
  if (flags.underline) marks.push({ type: 'underline' });
  if (flags.highlight) marks.push({ type: 'highlight' });
  return marks.length ? marks : undefined;
}

function cellToNodeJSON(cell: TableCellData, isHeader: boolean): JSONContent {
  const runs = effectiveRunsGeneric(cell);
  return {
    type: isHeader ? 'tableHeader' : 'tableCell',
    attrs: {
      colspan: Math.max(1, cell.colSpan || 1),
      rowspan: Math.max(1, cell.rowSpan || 1),
      // Widths live in `tableStyle.colWidths` (percentages) — the one unit the
      // document, the variant and the .docx all speak. Per-cell pixels are the
      // framework resizer's working state and are folded away as soon as a
      // drag lands, so a rebuilt document deliberately carries none.
      colwidth: null,
      align: cell.align || null,
      valign: cell.valign || null,
      bgColor: cell.bgColor || null,
      bgOpacity: cell.bgOpacity ?? null,
      borders: normalizeCellBorders(cell.borders),
    },
    content: [
      {
        type: 'specCellParagraph',
        ...(runs.length
          ? {
              content: runs.map((r) => ({
                type: 'text',
                text: r.text,
                ...(marksForRun(r) ? { marks: marksForRun(r) } : {}),
              })),
            }
          : {}),
      },
    ],
  };
}

/** Build the full `table` JSONContent node for a `blockType: 'table'`
 *  ContentBlock — the table half of schema.ts's `blockToNode`. */
export function tableDataToNodeJSON(
  b: ContentBlock,
  baseAttrs: {
    blockId: string;
    level: number;
    continuation: boolean;
    excluded: boolean;
    meta: BlockMeta;
  }
): JSONContent {
  const td = b.tableData;
  const rows = td?.cells?.length ? td.cells : [[{ text: '' }]];
  const style: TableStyleAttrs = {};
  for (const k of TABLE_STYLE_KEYS) {
    const v = (td as unknown as Record<string, unknown> | undefined)?.[k];
    if (v !== undefined) (style as Record<string, unknown>)[k] = v;
  }
  const headerRows = td?.headerRows || 0;
  return {
    type: TABLE_NODE_NAME,
    attrs: {
      ...baseAttrs,
      text: b.text || '',
      numbered: !!td?.numbered,
      tableStyle: style,
      tableMeta: {},
    },
    content: rows.map((row, ri) => ({
      type: 'tableRow',
      attrs: { minHeight: td?.rowHeights?.[ri] ?? null },
      content: row.map((cell) => cellToNodeJSON(cell, ri < headerRows)),
    })),
  };
}

// ─── ProseMirror JSON → TableData ───

function cellFromNodeJSON(c: JSONContent): TableCellData {
  const paras = (c.content || []).filter((n) => n.type === 'specCellParagraph');
  const flatRuns: Array<{ text: string } & MarkFlags> = [];
  paras.forEach((p, i) => {
    if (i > 0)
      flatRuns.push({ text: '\n', bold: false, italic: false, underline: false, highlight: false });
    flatRuns.push(...nodeRunsFromJSON(p.content));
  });
  const folded = foldRunsGeneric(flatRuns);
  const attrs = (c.attrs || {}) as Record<string, unknown>;
  const colSpan = Math.max(1, (attrs.colspan as number) || 1);
  const rowSpan = Math.max(1, (attrs.rowspan as number) || 1);
  return {
    text: folded.text,
    ...(folded.bold ? { bold: true } : {}),
    ...(folded.italic ? { italic: true } : {}),
    ...(folded.underline ? { underline: true } : {}),
    ...(folded.highlight ? { highlight: true } : {}),
    ...(folded.runs ? { runs: folded.runs } : {}),
    ...(attrs.align ? { align: attrs.align as TableCellData['align'] } : {}),
    ...(attrs.valign ? { valign: attrs.valign as TableCellData['valign'] } : {}),
    ...(attrs.bgColor ? { bgColor: attrs.bgColor as string } : {}),
    ...(attrs.bgOpacity !== null && attrs.bgOpacity !== undefined
      ? { bgOpacity: attrs.bgOpacity as number }
      : {}),
    ...((): { borders?: TableCellBorders } => {
      const b = normalizeCellBorders(attrs.borders as TableCellBorders | null);
      return b ? { borders: b } : {};
    })(),
    ...(colSpan > 1 ? { colSpan } : {}),
    ...(rowSpan > 1 ? { rowSpan } : {}),
  };
}

/** A row counts as a header row when EVERY cell in it is a `tableHeader` —
 *  exactly what the framework's own `toggleHeaderRow` command produces. */
function countLeadingHeaderRows(rows: JSONContent[]): number {
  let n = 0;
  for (const row of rows) {
    const cells = row.content || [];
    if (cells.length && cells.every((c) => c.type === 'tableHeader')) n++;
    else break;
  }
  return n;
}

/** Column widths, live from the framework's own resizer when a resize has
 *  actually happened (any cell in row 0 carries a non-null `colwidth`); the
 *  stored/authored percentages otherwise — so a freshly loaded, never-
 *  resized table round-trips its `colWidths` byte-identical.
 *
 *  In the live editor the normalizer plugin folds a drag away before this ever
 *  runs; this is the safety net for pixels that arrive some other way (a
 *  pasted HTML table, an older document). It is NOT lossy: a column nobody
 *  dragged keeps the share it had instead of being averaged flat. */
function deriveLiveColWidths(
  rows: JSONContent[],
  gridCols: number,
  storedPct: number[] | undefined
): number[] | undefined {
  const first = rows[0];
  if (!first || gridCols <= 0) return undefined;
  const cells = first.content || [];
  const px: number[] = new Array(gridCols).fill(0);
  let any = false;
  let col = 0;
  for (const c of cells) {
    const attrs = (c.attrs || {}) as Record<string, unknown>;
    const span = Math.max(1, (attrs.colspan as number) || 1);
    const cw = (attrs.colwidth as number[] | null) || null;
    for (let j = 0; j < span; j++, col++) {
      const w = cw && cw[j] ? cw[j] : 0;
      if (w) any = true;
      if (col < gridCols) px[col] = w;
    }
  }
  if (!any) return undefined;
  return foldColPx(storedPct, px, [], 0);
}

/** Build the `TableData` half of schema.ts's `nodeToBlock` for a `table`
 *  JSONContent node — the grid (rows/cols/cells/headerRows) is always
 *  derived fresh from the live content; style rides through `tableStyle`. */
export function nodeToTableData(node: JSONContent): TableData {
  const rows = node.content || [];
  const style = (node.attrs?.tableStyle || {}) as TableStyleAttrs;
  const cells: TableCellData[][] = rows.map((row) => (row.content || []).map(cellFromNodeJSON));
  const headerRows = countLeadingHeaderRows(rows);
  const gridCols = rows.reduce((max, row) => {
    const sum = (row.content || []).reduce((s, c) => {
      const attrs = (c.attrs || {}) as Record<string, unknown>;
      return s + Math.max(1, (attrs.colspan as number) || 1);
    }, 0);
    return Math.max(max, sum);
  }, 0);
  const liveColWidths = deriveLiveColWidths(rows, gridCols, style.colWidths);
  const colWidths = liveColWidths ?? style.colWidths;

  const out: TableData = {
    rows: rows.length,
    cols: gridCols || (cells[0]?.length ?? 1),
    cells,
  };
  if (colWidths !== undefined) out.colWidths = colWidths;
  if (style.edges && Object.keys(style.edges).length) out.edges = { ...style.edges };
  if (style.borderWidth !== undefined) out.borderWidth = style.borderWidth;
  if (style.borderColor !== undefined) out.borderColor = style.borderColor;
  if (style.borderStyle !== undefined)
    out.borderStyle = style.borderStyle as TableData['borderStyle'];
  if (style.innerBorderWidth !== undefined) out.innerBorderWidth = style.innerBorderWidth;
  if (style.innerBorderColor !== undefined) out.innerBorderColor = style.innerBorderColor;
  if (style.innerBorderStyle !== undefined)
    out.innerBorderStyle = style.innerBorderStyle as TableData['innerBorderStyle'];
  if (style.headerBgColor !== undefined) out.headerBgColor = style.headerBgColor;
  if (style.headerBgOpacity !== undefined) out.headerBgOpacity = style.headerBgOpacity;
  if (style.tableJustify !== undefined) out.tableJustify = style.tableJustify;
  if (style.autofit) out.autofit = true;
  if (style.width !== undefined) out.width = style.width;
  if (style.cellPadding !== undefined) out.cellPadding = style.cellPadding;
  const rowHeights = rows.map((row) => {
    const h = (row.attrs as Record<string, unknown> | undefined)?.minHeight;
    return typeof h === 'number' && h > 0 ? h : null;
  });
  if (rowHeights.some((h) => h !== null)) out.rowHeights = rowHeights;
  // A header row IS the repeat: Word has one flag (`w:tblHeader`) and it
  // means "repeat this row at the top of every page". The framework's
  // `toggleHeaderRow` writes that row's cells as `tableHeader`, the builder
  // emits `tableHeader: true` for them — so there is deliberately no second
  // "repeat header" switch to get out of sync with the first.
  if (headerRows > 0) out.headerRows = headerRows;
  if (node.attrs?.numbered) out.numbered = true;
  return out;
}

// ─── Insertion ───

/** A fresh table with a header row, at the given level — built entirely from
 *  the framework's own `createTable` utility (nothing hand-rolled), with our
 *  block identity + default style layered on afterward. Default 3×3 (every
 *  existing caller); the Tables toolbox's size picker (T1.2) is the only
 *  caller that passes `opts`. */
export function buildTableNode(
  schema: Schema,
  level: number,
  opts?: { rows?: number; cols?: number }
): PMNode {
  const rows = Math.max(1, opts?.rows ?? 3);
  const cols = Math.max(1, opts?.cols ?? 3);
  const base = createTable(schema, rows, cols, true);
  // The plain 3-column default keeps its exact legacy widths (byte-identical
  // for every pre-T1.2 caller); a picked size falls back to an even split.
  const colWidths = cols === 3 ? [33, 33, 34] : evenColPct(cols);
  return base.type.create(
    {
      blockId: newBlockId(),
      level,
      continuation: false,
      excluded: false,
      meta: {},
      text: '',
      numbered: false,
      tableStyle: { ...DEFAULT_NEW_TABLE_STYLE, colWidths },
      tableMeta: {},
    },
    base.content,
    base.marks
  );
}
